import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import AdminUpload from "./AdminUpload";
import UploadHistory from "./UploadHistory";
import Analytics from "./Analytics";

function AdminDashboard() {
  const [page, setPage] = useState("analytics");
  const [userData, setUserData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
      setIsLoading(false);
    };

    fetchUserData();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const renderPage = () => {
    switch (page) {
      case "analytics":
        return <Analytics />;
      case "upload":
        return <AdminUpload />;
      case "history":
        return <UploadHistory />;
      default:
        return <AdminUpload />;
    }
  };

  const getPageTitle = () => {
    switch (page) {
      case "analytics":
       return "User Analytics";
      case "upload":
        return "File Management";
      case "history":
        return "Upload History";
      default:
        return "Dashboard";
    }
  };

  const getPageDescription = () => {
    switch (page) {
      case "analytics":
       return "View key metrics and statistics about your user base";
      case "upload":
        return "Upload and manage specialty data files";
      case "history":
        return "View upload history and file status";
      default:
        return "Admin control panel";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white/90 backdrop-blur-sm shadow-sm border-b">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-lg mr-3 shadow-lg"></div>
            <h1 className="text-xl font-semibold text-gray-900">Admin Panel</h1>
          </div>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSidebarOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className={`${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white/90 backdrop-blur-sm shadow-xl border-r border-gray-200 transition-transform duration-300`}>
          {/* Sidebar Header */}
          <div className="hidden lg:block p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-indigo-100 text-sm">System Management</p>
              </div>
            </div>
          </div>

          {/* Admin Profile Section */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-br from-white to-indigo-50">
            <div className="flex items-center">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {userData?.name ? userData.name.charAt(0).toUpperCase() : "A"}
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div className="ml-4">
                <p className="text-lg font-semibold text-gray-900">
                  {userData?.name || "Administrator"}
                </p>
                <p className="text-sm text-gray-500">{userData?.email || auth.currentUser?.email}</p>
                <div className="flex items-center mt-1">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                  <span className="text-xs text-indigo-600 font-medium">Admin Access</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
           <nav className="flex-1 p-4">
            <div className="space-y-2">
              <button
                onClick={() => setPage("analytics")}
                className={`w-full group flex items-center px-4 py-4 text-left rounded-xl transition-all duration-200 ${
                  page === "analytics"
                    ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/25"
                    : "text-gray-700 hover:bg-white hover:shadow-md"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-all duration-200 ${
                  page === "analytics" ? "bg-white/20" : "bg-teal-50 group-hover:bg-teal-100"
                }`}>
                  <svg className={`w-5 h-5 ${page === "analytics" ? "text-white" : "text-teal-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <span className="font-semibold block">User Analytics</span>
                  <span className={`text-xs ${page === "analytics" ? "text-teal-100" : "text-gray-500"}`}>
                    View user metrics
                  </span>
                </div>
              </button>
              <button
                onClick={() => setPage("upload")}
                className={`w-full group flex items-center px-4 py-4 text-left rounded-xl transition-all duration-200 ${
                  page === "upload"
                    ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/25"
                    : "text-gray-700 hover:bg-white hover:shadow-md"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-all duration-200 ${
                  page === "upload" ? "bg-white/20" : "bg-indigo-50 group-hover:bg-indigo-100"
                }`}>
                  <svg className={`w-5 h-5 ${page === "upload" ? "text-white" : "text-indigo-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <span className="font-semibold block">File Upload</span>
                  <span className={`text-xs ${page === "upload" ? "text-indigo-100" : "text-gray-500"}`}>
                    Manage data files
                  </span>
                </div>
              </button>

              <button
                onClick={() => setPage("history")}
                className={`w-full group flex items-center px-4 py-4 text-left rounded-xl transition-all duration-200 ${
                  page === "history"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-gray-700 hover:bg-white hover:shadow-md"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-all duration-200 ${
                  page === "history" ? "bg-white/20" : "bg-blue-50 group-hover:bg-blue-100"
                }`}>
                  <svg className={`w-5 h-5 ${page === "history" ? "text-white" : "text-blue-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <span className="font-semibold block">Upload History</span>
                  <span className={`text-xs ${page === "history" ? "text-blue-100" : "text-gray-500"}`}>
                    View activity logs
                  </span>
                </div>
              </button>
            </div>

            {/* System Stats */}
            <div className="mt-8 p-4 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">System Status</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Storage Used</span>
                  <span className="text-sm font-bold text-green-600">45%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Active Files</span>
                  <span className="text-sm font-bold text-blue-600">12</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Last Upload</span>
                  <span className="text-sm font-bold text-gray-600">2h ago</span>
                </div>
              </div>
            </div>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-4 py-3 text-left rounded-xl text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-all duration-200 hover:shadow-md"
            >
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-gray-200 transition-colors">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <span className="font-semibold">Sign Out</span>
            </button>
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}

        {/* Main Content */}
        <div className="flex-1">
          {/* Main Header */}
          <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-gray-200">
            <div className="px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-light text-gray-900">{getPageTitle()}</h1>
                  <p className="text-gray-600 mt-1">{getPageDescription()}</p>
                </div>
                
                {/* Status Indicator */}
                <div className="hidden md:flex items-center space-x-4">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2 rounded-xl border border-green-200">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                      <span className="text-sm font-semibold text-green-700">System Online</span>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-indigo-50 to-indigo-50 px-4 py-2 rounded-xl border border-indigo-200">
                    <span className="text-sm font-semibold text-indigo-700">
                      Admin: {userData?.name?.split(' ')[0] || 'User'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              {/* Page Content */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 min-h-[600px]">
                <div className="p-6 lg:p-8">
                  {renderPage()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;